// Red banner shown wherever Beds24 data is missing: explains why and lets the super admin paste a fresh invite code on the spot.
import React, { useEffect, useState } from 'react';
import { KeyRound, Loader2, PlugZap, RefreshCw } from 'lucide-react';
import { apiCall } from '../../pages/admin/_shared';

export const Beds24ReconnectBanner = ({ error, userRole, onReconnected, dataSource }) => {
  const [health, setHealth] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const canFix = ['super_admin', 'admin'].includes(userRole);

  useEffect(() => {
    let alive = true;
    apiCall('/api/admin/beds24/health').then((h) => { if (alive) setHealth(h); }).catch(() => {});
    return () => { alive = false; };
  }, [error]);

  const connect = async () => {
    if (!code.trim()) return;
    setBusy(true); setMsg('');
    try {
      const r = await apiCall('/api/admin/beds24/connect', { method: 'POST', body: JSON.stringify({ invite_code: code.trim() }) });
      setMsg(`Connected to Beds24 (scopes: ${(r.scopes || []).join(', ') || 'ok'}). Loading bookings…`);
      setCode('');
      if (onReconnected) await onReconnected();
    } catch (e) { setMsg(String(e.message || e)); }
    setBusy(false);
  };

  const retry = async () => { setBusy(true); try { if (onReconnected) await onReconnected(); } finally { setBusy(false); } };

  const isCredit = health?.last_error?.status === 429;
  const mirror = dataSource === 'mirror';
  const tone = isCredit || mirror ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-red-300 bg-red-50 text-red-900';
  const title = isCredit ? 'Beds24 API credit limit reached — showing the local copy of the bookings.' : (mirror ? 'Beds24 did not answer — showing the local copy of the bookings.' : 'Beds24 is not connected on this server — the calendar cannot load live bookings.');
  const mirrorInfo = health?.mirror ? ` Local copy: ${health.mirror.total} bookings${health.mirror.last_sync?.at ? `, synced ${String(health.mirror.last_sync.at).slice(0, 16).replace('T', ' ')} UTC` : ''} (refreshes every 10 min while Beds24 answers).` : '';
  return (
    <div className={`rounded-xl border-2 p-4 mb-4 text-sm ${tone}`} data-testid="beds24-reconnect-banner">
      <p className="font-bold flex items-center gap-2"><PlugZap className="w-4 h-4" /> {title}</p>
      <p className="text-xs mt-1 opacity-90">{error}{health ? ` Token: ${health.source === 'mongo' ? `connected ${String(health.connected_at || '').slice(0, 10)}` : (health.has_env_token ? 'server .env' : 'none stored on this server')} · property ${health.property_id}.` : ''}{mirrorInfo}</p>
      {isCredit && <p className="text-xs mt-2">Beds24 refills API credits automatically every few minutes — the calendar switches back to live data by itself. Avoid re-running the full history import; import only the years you need.</p>}
      {canFix && !isCredit ? (
        <div className="mt-3 space-y-2">
          <p className="text-xs font-semibold">Fix in 2 minutes: Beds24 → Settings → Apps &amp; Integrations → API → Invite codes → Generate (scopes read/write bookings, read:bookings-personal, read/write inventory, read:properties) → paste the code here.</p>
          <div className="flex flex-wrap gap-2 items-center">
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Paste Beds24 invite code" className="flex-1 min-w-[260px] border border-red-300 rounded-lg px-3 py-2 text-sm bg-white text-slate-900" data-testid="beds24-banner-code" />
            <button type="button" onClick={connect} disabled={busy || !code.trim()} className="inline-flex items-center gap-1.5 bg-red-700 hover:bg-red-800 text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50" data-testid="beds24-banner-connect">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Connect Beds24
            </button>
            <button type="button" onClick={retry} disabled={busy} className="inline-flex items-center gap-1 border border-red-300 rounded-lg px-3 py-2 text-xs hover:bg-white" data-testid="beds24-banner-retry"><RefreshCw className="w-3.5 h-3.5" /> Retry</button>
          </div>
          {msg && <p className="text-xs font-semibold" data-testid="beds24-banner-msg">{msg}</p>}
        </div>
      ) : (
        <p className="text-xs mt-2">Ask the super admin to re-connect Beds24 (Admin → Guest Emails → Beds24 connection).</p>
      )}
    </div>
  );
};
